import { useCallback, useEffect, useRef, useState } from 'react';

import { DefaultError, DefaultLoading } from '../../components';
import { Module, ModuleLoaderAnyDependencies } from '../../types';
import { useModuleManager } from './ModuleLoaderContext';

/**
 * {@link https://app.getguru.com/card/ckKonEqi/Lazyloaded-modules-with-mewsmodules}
 */
const createLoadableComponent =
    <
        ModuleType extends Module & { components: { [name in ComponentName]: React.ComponentType<$TSFixMe> } },
        ComponentName extends keyof ModuleType['components'],
        Props = React.ComponentProps<ModuleType['components'][ComponentName]>,
    >({
        loader,
        component,
        LoadingComponent = DefaultLoading,
        ErrorComponent = DefaultError,
    }: {
        loader: ModuleLoaderAnyDependencies<ModuleType>;
        component: ComponentName;
        LoadingComponent?: React.ComponentType;
        ErrorComponent?: React.ComponentType<{ retry(): void }>;
    }): React.FC<Props & JSX.IntrinsicAttributes> =>
    (props) => {
        const moduleRef = useRef<ModuleType | null>(null);
        const moduleManager = useModuleManager();
        const [isLoading, setLoading] = useState(true);

        const tryLoadModule = useCallback(async () => {
            setLoading(true);
            try {
                moduleRef.current = await moduleManager.addModule(loader);
                // eslint-disable-next-line @mews/no-empty-catch
            } catch (error) {
                // errors are logged in moduleManager
            } finally {
                setLoading(false);
            }
        }, [moduleManager]);

        useEffect(() => {
            void tryLoadModule();
        }, [tryLoadModule]);

        if (isLoading) {
            return <LoadingComponent />;
        }
        if (moduleRef.current) {
            const Component: React.ComponentType<Props> = moduleRef.current.components[component];
            return <Component {...props} />;
        }
        return <ErrorComponent retry={tryLoadModule} />;
    };

export default createLoadableComponent;
